import { Grid } from 'semantic-ui-react'
import { FormikProvider, useFormik } from 'formik'
import { DateTime } from 'luxon'
import * as yup from 'yup'

import {
  FederalIncomeTaxEstimateDetails,
  createSingleFederalIncomeTaxDetails,
} from '../federalIncomeTaxDetailsActions'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import {
  Button,
  FormikCheckbox,
  FormikDateInput,
  FormikInput,
  FormikTextArea,
  GridRowColumn,
  Modal,
  makeDateSchema,
  makeNumberSchema,
  getFieldNames,
} from '@/componentsV1/BaseComponents'
import { getCurrentUser } from '@/selectors/user.selectors'
import {
  hasCorrectFilingStatuses,
  isCorrectStandardDeductionFormat,
} from '../../IncomeTaxDetailsHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'

interface Props {
  modalOpen: boolean
  onClose: (newDetails?: FederalIncomeTaxEstimateDetails) => void
}

export const FederalIncomeTaxDetailsModal = ({ onClose, modalOpen }: Props) => {
  const dispatch = useAppDispatch()

  const currentUser = useReselector(getCurrentUser)

  const formik = useFormik({
    initialValues: {
      effectiveFrom: '',
      current: false,
      standardDeduction: '',
      dependentDeductionInCents: '',
      deductibleSelfEmploymentRate: '',
      qbiDeductionRate: '',
      qbiDeductionIncomeLimitInCents: '',
      selfEmploymentReductionRate: '',
      socialSecurityTaxMaxIncomeInCents: '',
      socialSecurityTaxRate: '',
      medicareTaxRate: '',
      taxRateBrackets: '',
    },
    onSubmit: async ({
      effectiveFrom,
      current,
      standardDeduction,
      dependentDeductionInCents,
      deductibleSelfEmploymentRate,
      qbiDeductionRate,
      qbiDeductionIncomeLimitInCents,
      selfEmploymentReductionRate,
      socialSecurityTaxMaxIncomeInCents,
      socialSecurityTaxRate,
      medicareTaxRate,
      taxRateBrackets,
    }) => {
      if (
        !standardDeduction ||
        !deductibleSelfEmploymentRate ||
        !deductibleSelfEmploymentRate ||
        !qbiDeductionRate ||
        !qbiDeductionIncomeLimitInCents ||
        !selfEmploymentReductionRate ||
        !socialSecurityTaxMaxIncomeInCents ||
        !socialSecurityTaxRate ||
        !medicareTaxRate ||
        !taxRateBrackets ||
        !currentUser
      ) {
        return
      }

      //make sure the JSON parsing works properly
      try {
        const data = await createSingleFederalIncomeTaxDetails({
          effectiveFrom: DateTime.fromFormat(
            effectiveFrom,
            DATE_FORMATS_LUXON.INPUT,
            { zone: 'utc' }
          ).toJSDate(),
          current,
          standardDeduction: standardDeduction
            ? JSON.parse(standardDeduction)
            : undefined,
          dependentDeductionInCents: Number(dependentDeductionInCents),
          deductibleSelfEmploymentRate: Number(deductibleSelfEmploymentRate),
          qbiDeductionRate: Number(qbiDeductionRate),
          qbiDeductionIncomeLimitInCents: Number(
            qbiDeductionIncomeLimitInCents
          ),
          selfEmploymentReductionRate: Number(selfEmploymentReductionRate),
          socialSecurityTaxMaxIncomeInCents: Number(
            socialSecurityTaxMaxIncomeInCents
          ),
          socialSecurityTaxRate: Number(socialSecurityTaxRate),
          medicareTaxRate: Number(medicareTaxRate),
          taxRateBrackets: taxRateBrackets
            ? JSON.parse(taxRateBrackets)
            : undefined,
          updatedBy: currentUser.id,
        })(dispatch)

        if (data) {
          onClose(data)
        }
      } catch (err) {
        console.error(err)
      }
    },
  })

  const { submitForm } = formik
  const fieldNames = getFieldNames(formik)

  return (
    <FormikProvider value={formik}>
      <Modal open={modalOpen} closeIcon onClose={() => onClose()}>
        <Modal.Header>Create new federal income tax details</Modal.Header>
        <Modal.Content>
          <Grid>
            <GridRowColumn>
              <FormikCheckbox
                label="Use in current QTE calculations"
                name={fieldNames.current}
              />
            </GridRowColumn>
            <GridRowColumn>
              <FormikDateInput
                label="Effective From"
                name={fieldNames.effectiveFrom}
                schema={makeDateSchema()}
                required
              />
            </GridRowColumn>
            <GridRowColumn>
              <FormikTextArea
                label="Standard Deduction"
                name={fieldNames.standardDeduction}
                required
                schema={yup
                  .string()
                  .nullable()
                  .test(
                    'standardDeduction check',
                    'Standard Deduction must be in the proper format',
                    (val) => {
                      // undefined or empty string is not allowed
                      if (!val) {
                        return false
                      }

                      try {
                        const parsedObject = JSON.parse(val)
                        return isCorrectStandardDeductionFormat(parsedObject)
                      } catch (_) {
                        return false
                      }
                    }
                  )}
              />
            </GridRowColumn>
            <GridRowColumn>
              <FormikInput
                label="Dependent Deduction in Cents"
                name={fieldNames.dependentDeductionInCents}
                componentType="number"
                schema={makeNumberSchema({ allowedDecimals: 0 })}
                prefix=""
                decimalScale={0}
              />
            </GridRowColumn>
            <GridRowColumn>
              <FormikInput
                label="Deductible Self Employment Rate"
                name={fieldNames.deductibleSelfEmploymentRate}
                componentType="number"
                schema={makeNumberSchema({
                  maxNumber: 1,
                  allowedDecimals: 4,
                })}
                required
                prefix=""
                decimalScale={4}
              />
            </GridRowColumn>
            <GridRowColumn>
              <FormikInput
                label="QBI Deduction Income Limit in Cents"
                name={fieldNames.qbiDeductionIncomeLimitInCents}
                componentType="number"
                schema={makeNumberSchema({ allowedDecimals: 0 })}
                required
                prefix=""
                decimalScale={0}
              />
            </GridRowColumn>
            <GridRowColumn>
              <FormikInput
                label="QBI Deduction Rate"
                name={fieldNames.qbiDeductionRate}
                componentType="number"
                schema={makeNumberSchema({
                  maxNumber: 1,
                  allowedDecimals: 4,
                })}
                required
                prefix=""
                decimalScale={4}
              />
            </GridRowColumn>
            <GridRowColumn>
              <FormikInput
                label="Self Employment Reduction Rate"
                name={fieldNames.selfEmploymentReductionRate}
                componentType="number"
                schema={makeNumberSchema({
                  maxNumber: 1,
                  allowedDecimals: 4,
                })}
                required
                prefix=""
                decimalScale={4}
              />
            </GridRowColumn>
            <GridRowColumn>
              <FormikInput
                label="Social Security Tax Max Income in Cents"
                name={fieldNames.socialSecurityTaxMaxIncomeInCents}
                componentType="number"
                schema={makeNumberSchema({ allowedDecimals: 0 })}
                required
                prefix=""
                decimalScale={0}
              />
            </GridRowColumn>
            <GridRowColumn>
              <FormikInput
                label="Social Security Tax Rate"
                name={fieldNames.socialSecurityTaxRate}
                componentType="number"
                schema={makeNumberSchema({
                  maxNumber: 1,
                  allowedDecimals: 4,
                })}
                required
                prefix=""
                decimalScale={4}
              />
            </GridRowColumn>
            <GridRowColumn>
              <FormikInput
                label="Medicare Tax Rate"
                name={fieldNames.medicareTaxRate}
                componentType="number"
                schema={makeNumberSchema({
                  maxNumber: 1,
                  allowedDecimals: 4,
                })}
                required
                prefix=""
                decimalScale={4}
              />
            </GridRowColumn>
            <GridRowColumn>
              <FormikTextArea
                label="Tax Rate Brackets"
                name={fieldNames.taxRateBrackets}
                required
                schema={yup
                  .string()
                  .nullable()
                  .test(
                    'taxBracket check',
                    'Tax brackets must be in proper format',
                    (val) => {
                      // If this field shows up, it must be populated
                      if (!val) {
                        return false
                      }

                      let parsedObject: unknown

                      try {
                        parsedObject = JSON.parse(val)
                        //There are backend validations for the shape, so just do basic
                        //checks around filing statuses here
                        return hasCorrectFilingStatuses(parsedObject)
                      } catch (_) {
                        return false
                      }
                    }
                  )}
              />
            </GridRowColumn>
          </Grid>
        </Modal.Content>
        <Modal.Actions>
          <Button variant="secondary" onClick={() => onClose()}>
            Cancel
          </Button>
          <Button onClick={submitForm}>Submit</Button>
        </Modal.Actions>
      </Modal>
    </FormikProvider>
  )
}
